import PropTypes from "prop-types";
import { Breadcrumb as AntBreadcrumb } from "antd";
import { RightOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import { Link, useLocation } from "react-router-dom";

const Breadcrumb = ({ items = null, textColor = "black" }) => {
  const location = useLocation();

  // Common home link component
  const homeLink = (
    <Link
      to="/"
      className={`!text-${textColor} text-small-xs inline-flex items-center leading-none hover:no-underline hover:text-${textColor} !hover:text-${textColor} hover:bg-transparent !hover:bg-transparent bg-transparent !p-0`}>
      <WebIcon
        icon="BreadcrumbHome"
        className={`w-[15px] h-[15px] relative -top-[3px] mr-2`}
        color={textColor}
      />{" "}
      <span className={`text-${textColor}`}>Home</span>
    </Link>
  );

  // If manual items are provided, use them
  if (items) {
    const breadcrumbItems = [
      {
        title: homeLink,
        key: "home",
      },
      ...items.map((item, index) => ({
        title: item.link ? (
          <Link
            to={item.link}
            className={`text-${textColor} text-small-xs font-bold ${
              item.color ? `text-${item.color}` : ""
            }`}>
            {item.title}
          </Link>
        ) : (
          <span className={`text-${textColor} text-small-xs font-bold`}>
            {item.title}
          </span>
        ),
        key: `item-${index}`,
      })),
    ];

    return (
      <AntBreadcrumb
        className="mb-4"
        separator={
          <RightOutlined className={`text-small-xs text-${textColor} w-[10px] h-[10px] justify-center items-center mt-2`} />
        }
        items={breadcrumbItems}
      />
    );
  }

  // Generate automatic breadcrumbs from current path
  const pathSnippets = location.pathname.split("/").filter((i) => i);

  const isMongoId = (str) => /^[0-9a-f]{24}$/i.test(str);

  // Filter out MongoDB IDs from display but keep their position in the URL construction
  const displayableSnippets = pathSnippets.filter(
    (snippet) => !isMongoId(snippet)
  );

  const autoGeneratedItems = [
    {
      title: homeLink,
      key: "home",
    },
    ...displayableSnippets.map((snippet, index) => {
      // Calculate the correct URL by finding this snippet's position in the original path
      const snippetPosition = pathSnippets.findIndex((s) => s === snippet);
      const url = `/${pathSnippets.slice(0, snippetPosition + 1).join("/")}`;

      const title = snippet
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());

      // Apply consistent styling for automatic breadcrumbs
      return {
        title: (
          <Link to={url} className={`text-${textColor}`}>
            {title}
          </Link>
        ),
        key: `path-${index}`,
      };
    }),
  ];

  // Update the last item to be non-clickable
  if (autoGeneratedItems.length > 1) {
    const lastIndex = autoGeneratedItems.length - 1;
    autoGeneratedItems[lastIndex] = {
      title: (
        <span className={`text-${textColor} text-small-xs font-bold`}>
          {autoGeneratedItems[lastIndex].title.props.children}
        </span>
      ),
      key: `path-${lastIndex}`,
    };
  }

  return (
    <AntBreadcrumb
      className="mb-4"
      separator={<RightOutlined className={`text-small-xs text-${textColor}`} />}
      items={autoGeneratedItems}
    />
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
  textColor: PropTypes.string,
};

export default Breadcrumb;
